import { Component, input, output } from '@angular/core';
import { Design } from 'src/app/shared/models/design.model';
import { formatUnitNumber } from 'src/app/shared/helpers/unit-number-format.helper';
import { UnitPrecisionOverrides } from 'src/app/shared/helpers/unit-precision.helper';
import { Tubular } from 'src/app/wellbore-inputs/components/well-configuration/models';
import { User } from '../user-admin-page/user-model';

type TubularOption = Tubular & {
  outsideDiameter?: number;
  stringSections?: readonly {
    outsideDiameter?: number;
    pipe?: { outsideDiameter?: number } | null;
  }[];
};

@Component({
  selector: 'app-toolbar-main-controls',
  templateUrl: './toolbar-main-controls.component.html',
  styles: `
    .calc-btns {
      display: flex;
      height: 24px;

      img {
        height: 10px;
        width: 12px;
      }

      .perical-icon {
        filter: brightness(0) saturate(100%) invert(41%) sepia(47%) saturate(3030%) hue-rotate(347deg) brightness(103%) contrast(101%);
      }

      .perinix-icon {
        filter: brightness(0) saturate(100%) invert(61%) sepia(30%) saturate(877%) hue-rotate(73deg) brightness(106%) contrast(83%);
      }

      .perivis-icon {
        filter: brightness(0) saturate(100%) invert(64%) sepia(82%) saturate(1799%) hue-rotate(142deg) brightness(106%) contrast(104%);
      }
    }
  `,
  standalone: false
})
export class ToolbarMainControlsComponent {
  readonly design = input<Design | null>(null);
  readonly user = input<User | null>(null);
  readonly tubulars = input<TubularOption[]>([]);
  readonly selectedTubular = input<TubularOption | null>(null);
  readonly shortLengthUnit = input('in');
  readonly pipeDimensionPrecisionOverrides = input<UnitPrecisionOverrides>({});
  readonly isTubularReady = input<boolean>(false);
  readonly isPericalDisabled = input<boolean>(false);
  readonly isPerinixDisabled = input<boolean>(false);
  readonly isPerivisDisabled = input<boolean>(false);
  readonly calculatingPerivis = input<boolean>(false);
  readonly calculatingPerical = input<boolean>(false);
  readonly calculatingPerinix = input<boolean>(false);
  readonly isDarkMode = input<boolean>(true);

  readonly tubularChange = output<Tubular | null>();
  readonly calculateStress = output<void>();
  readonly calculateThermal = output<void>();
  readonly calculateApb = output<void>();

  readonly formatTubularOption = (tubular: TubularOption): string => {
    const unit = this.shortLengthUnit();
    const precisionOverrides = this.pipeDimensionPrecisionOverrides();
    const name = tubular.name.replace(/\d+\.\d+/g, value =>
      formatUnitNumber(Number(value), unit, undefined, undefined, precisionOverrides)
    );
    const outsideDiameters = tubular.stringSections?.map(section =>
      section.pipe?.outsideDiameter ?? section.outsideDiameter
    ).filter((diameter): diameter is number => typeof diameter === 'number' && Number.isFinite(diameter));
    const diameters = outsideDiameters?.length
      ? outsideDiameters
      : typeof tubular.pipe?.outsideDiameter === 'number'
        ? [tubular.pipe.outsideDiameter]
        : typeof tubular.outsideDiameter === 'number'
          ? [tubular.outsideDiameter]
          : [];

    if (!diameters.length) {
      return name;
    }

    const unitLabel = unit === 'in' ? '"' : unit;
    const formattedDiameters = diameters.map(diameter =>
      formatUnitNumber(diameter, unit, undefined, undefined, precisionOverrides)
    );
    return `${name} (${formattedDiameters.join(' x ')}${unitLabel})`;
  };

  copyDesignName(): void {
    const name = this.design()?.name;
    if (name) navigator.clipboard.writeText(name);
  }

  onTubularSelectionChange(tubularId: string | null): void {
    const tubular = this.tubulars().find(item => item.id === tubularId) ?? null;
    this.tubularChange.emit(tubular);
  }
}
